import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TextField, Select, MenuItem, Divider, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import React from 'react';

import { SectionHeading, FieldLabel } from './components';
import ResourceTypeSelector from './ResourceTypeSelector';
import { type ResourceDataType, type ResourceType } from './types';

import EssealPhoneInput from '@/components/EssealPhoneInput';
import { GREY } from '@/theme/palette';

const PLACEHOLDERS: Record<
  ResourceType,
  {
    name: string;
    reference: string;
    contactName: string;
    contactEmail: string;
  }
> = {
  Human: {
    name: 'e.g. John Smith, Site Manager',
    reference: 'e.g. employee number, NI number',
    contactName: 'e.g. John Smith',
    contactEmail: 'e.g. john@company.com',
  },
  Equipment: {
    name: 'e.g. 3.5t Excavator, Hilti Breaker, Generator',
    reference: 'e.g. VIN, serial number, asset no.',
    contactName: 'e.g. Yard Manager',
    contactEmail: 'e.g. yard@company.com',
  },
  Material: {
    name: 'e.g. cement, tar, rebar',
    reference: 'e.g. item code, SKU',
    contactName: 'e.g. Supplier Contact',
    contactEmail: 'e.g. orders@supplier.com',
  },
  Subcontractor: {
    name: 'e.g. Acme Groundworks Ltd',
    reference: 'e.g. supplier code, company no.',
    contactName: 'e.g. Jane Roberts',
    contactEmail: 'e.g. jane@acme.co.uk',
  },
};

interface BasicsProps {
  data: ResourceDataType;
  setData: <K extends keyof ResourceDataType>(key: K, value: ResourceDataType[K]) => void;
  disabled?: boolean;
}

const Basics: React.FC<BasicsProps> = ({ data, setData, disabled = false }) => {
  const currentPlaceholder = PLACEHOLDERS[data.type] || PLACEHOLDERS.Human;
  const contactSubtitle =
    data.type === 'Equipment'
      ? 'Add the main contact details for this resource (if applicable).'
      : 'Add the main contact details for this resource.';

  const handleTypeChange = (newType: ResourceType) => {
    setData('type', newType);
    if (newType === 'Subcontractor' && (!data.source || data.source === 'Internal')) {
      setData('source', 'External');
    } else if (newType !== 'Subcontractor' && !data.source) {
      setData('source', 'Internal');
    }
  };

  return (
    <Box sx={{ px: 3.5, pt: 2.5, pb: 2.5, backgroundColor: '#FFFFFF', borderRadius: 2 }}>
      <SectionHeading title="1.  Basic details" hint="Select the type of resource and provide the core information." />

      <ResourceTypeSelector resourceType={data.type} setResourceType={handleTypeChange} disabled={disabled} />

      <Box sx={{ mb: 2.25 }}>
        <FieldLabel required>Resource name</FieldLabel>
        <TextField
          fullWidth
          disabled={disabled}
          placeholder={currentPlaceholder.name}
          value={data.name}
          onChange={(event) => setData('name', event.target.value)}
        />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel required>Source</FieldLabel>
          <Select
            fullWidth
            disabled={disabled}
            IconComponent={ExpandMoreIcon}
            value={data.source}
            displayEmpty
            onChange={(event) => setData('source', event.target.value as 'Internal' | 'External')}
          >
            <MenuItem value="" disabled>
              Select source
            </MenuItem>
            <MenuItem value="Internal">Internal</MenuItem>
            <MenuItem value="External">External</MenuItem>
          </Select>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel required>Unique reference / ID</FieldLabel>
          <TextField
            fullWidth
            disabled={disabled}
            placeholder={currentPlaceholder.reference}
            value={data.code}
            onChange={(event) => setData('code', event.target.value)}
          />
        </Box>
      </Stack>

      <Divider sx={{ mb: 2, borderColor: GREY }} />

      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Primary contact details</Typography>
      <Typography sx={{ fontSize: 12.5, color: GREY, mb: 1.75 }}>{contactSubtitle}</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>Contact name</FieldLabel>
          <TextField
            fullWidth
            disabled={disabled}
            placeholder={currentPlaceholder.contactName}
            value={data.contact_name}
            onChange={(event) => setData('contact_name', event.target.value)}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>Email address</FieldLabel>
          <TextField
            fullWidth
            disabled={disabled}
            placeholder={currentPlaceholder.contactEmail}
            value={data.contact_email}
            onChange={(event) => setData('contact_email', event.target.value)}
          />
        </Box>
      </Stack>

      <FieldLabel>Phone number</FieldLabel>
      <EssealPhoneInput
        size="lg"
        disabled={disabled}
        defaultCountry="GB"
        value={data.contact_phone}
        onChange={(e164) => {
          setData('contact_phone', e164 || '');
        }}
      />
    </Box>
  );
};

export default Basics;
